import { useEffect, useState } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Menu } from 'lucide-react'
import { Link, NavLink, Outlet, useLocation, useOutletContext, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { KnowledgeTabs } from '@/features/projects/layout/knowledge-tabs'
import {
  KNOWLEDGE_SECTION_ICON,
  KNOWLEDGE_SECTION_TITLE,
  PROJECT_NAV_ITEMS,
  findKnowledgeTab,
  isKnowledgeTabSection,
} from '@/features/projects/layout/project-nav'
import { ProjectProvisioningState } from '@/features/projects/components/project-provisioning-state'
import { getProject, unarchiveProject } from '@/features/projects/projects-api'
import { projectsQueryKey } from '@/features/projects/projects-query-keys'
import {
  unreadConversationsQueryKey,
  useUnreadConversations,
} from '@/features/projects/use-unread-conversations'
import { ErrorState } from '@/shared/components/common/error-state'
import { LoadingBlock } from '@/shared/components/common/loading-block'
import { PageHeader } from '@/shared/components/common/page-header'
import { Banner } from '@/shared/components/ui/banner'
import { Button } from '@/shared/components/ui/button'
import { CountBadge } from '@/shared/components/ui/count-badge'
import { PROJECT_NOT_READY_EVENT, getProjectNotReady } from '@/shared/api/errors'
import type { Project } from '@/shared/types/domain'
import { cn } from '@/shared/utils/cn'

interface ProjectOutletContext {
  project: Project
  openNav: () => void
}

export function useProjectContext(): ProjectOutletContext {
  return useOutletContext<ProjectOutletContext>()
}

const projectQueryKey = (projectId: string) => ['project', projectId] as const

export function ProjectLayout() {
  const { projectId } = useParams<{ projectId: string }>()
  const { unreadForProject, unreadLeadsForProject } = useUnreadConversations()
  const location = useLocation()
  const [navOpen, setNavOpen] = useState(false)

  const projectQuery = useQuery({
    enabled: Boolean(projectId),
    queryKey: projectQueryKey(projectId || ''),
    queryFn: () => getProject(projectId || ''),
    refetchInterval: (query) => {
      // Проект в сборке отвечает 423 — ждём его окончания прямо на открытой странице.
      if (getProjectNotReady(query.state.error)) {
        return 12_000
      }

      // Пересборка кабинет не закрывает, но баннер о ней должен погаснуть сам.
      return query.state.data?.provisioning_in_progress ? 30_000 : false
    },
  })

  const queryClient = useQueryClient()

  const unarchiveMutation = useMutation({
    mutationFn: () => unarchiveProject(projectId || ''),
    onSuccess: (updated) => {
      queryClient.setQueryData(projectQueryKey(updated.id), updated)
      void queryClient.invalidateQueries({ queryKey: projectsQueryKey })
      void queryClient.invalidateQueries({ queryKey: unreadConversationsQueryKey })
      toast.success('Проект возвращён из архива')
    },
  })

  const notReady = getProjectNotReady(projectQuery.error)
  const { refetch } = projectQuery

  // Подресурсы проекта отвечают 423, когда сборку запустили при открытой вкладке:
  // проект тогда лежит в кеше готовым, и страница показала бы ошибку загрузки
  // вместо заглушки. Перепрашиваем проект — заглушку нарисует ветка ниже.
  useEffect(() => {
    if (!projectId || notReady) {
      return
    }

    const onNotReady = (event: Event) => {
      if ((event as CustomEvent<{ projectId?: string }>).detail?.projectId === projectId) {
        void refetch()
      }
    }

    window.addEventListener(PROJECT_NOT_READY_EVENT, onNotReady)

    return () => window.removeEventListener(PROJECT_NOT_READY_EVENT, onNotReady)
  }, [notReady, projectId, refetch])

  if (projectQuery.isLoading) {
    return <LoadingBlock label="Загружаем проект..." />
  }

  const project = projectQuery.data

  if (notReady) {
    return (
      <ProjectProvisioningState
        action={
          <Link to="/projects">
            <Button variant="secondary">Ко всем проектам</Button>
          </Link>
        }
        blockReason={notReady.blockReason}
        layout="page"
        status={notReady.status}
      />
    )
  }

  if (!project) {
    return <ErrorState description="Проект не найден или недоступен." title="Проект не найден" />
  }

  const items = PROJECT_NAV_ITEMS.filter(
    (item) => item.section !== 'widget' || Boolean(project.widget_enabled),
  )
  const activeSection = location.pathname.split('/')[3] ?? ''
  const activeItem = PROJECT_NAV_ITEMS.find((item) => item.section === activeSection)
  const isChat = activeSection === 'conversations'
  const isKnowledge = isKnowledgeTabSection(activeSection)

  return (
    <div className="flex h-full min-h-0 gap-4">
      <button
        aria-label="Закрыть меню разделов"
        className={cn('fixed inset-0 z-30 bg-black/45 lg:hidden', navOpen ? 'block' : 'hidden')}
        onClick={() => setNavOpen(false)}
        type="button"
      />

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 w-60 flex-none overflow-y-auto border-r border-[var(--panel-border)] bg-[var(--panel)] p-3 transition lg:static lg:z-auto lg:translate-x-0 lg:rounded-2xl lg:border',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <nav className="space-y-1">
          {items.map((item) => (
            <NavLink
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition',
                  isActive
                    ? 'bg-[var(--color-accent)]/12 text-[var(--color-accent)]'
                    : 'text-[var(--color-text-soft)] hover:bg-[var(--panel-subtle)] hover:text-[var(--color-text)]',
                )
              }
              key={item.section}
              onClick={() => setNavOpen(false)}
              to={`/projects/${project.id}/${item.section}`}
            >
              <item.icon className="h-4 w-4 flex-none" />
              <span className="flex-1">{item.label}</span>
              {item.section === 'conversations' ? (
                <CountBadge count={unreadForProject(project.id)} />
              ) : item.section === 'leads' ? (
                <CountBadge count={unreadLeadsForProject(project.id)} />
              ) : null}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {project.archived_at ? (
          <Banner
            actions={
              <Button
                loading={unarchiveMutation.isPending}
                onClick={() => unarchiveMutation.mutate()}
                size="sm"
                variant="secondary"
              >
                Вернуть из архива
              </Button>
            }
            className="mb-3"
            title="Проект в архиве — бот не отвечает"
            tone="warning"
          >
            Виджет и уведомления молчат, автосборка не запускается. Диалоги и лиды видны только
            здесь.
          </Banner>
        ) : null}

        {project.provisioning_in_progress ? (
          <Banner className="mb-3" title="Идёт пересборка проекта" tone="warning">
            База знаний, каталог и промпты сейчас обновляются: данные могут меняться на глазах, а
            ответы бота — временно отличаться от привычных.
          </Banner>
        ) : null}

        {/* В диалогах гамбургер и имя проекта живут в шапке списка — вторая полоса там лишняя. */}
        {isChat ? null : (
          <div className="mb-3 flex items-center gap-2 lg:hidden">
            <button
              aria-label="Открыть меню разделов"
              className="rounded-lg border border-[var(--panel-border)] p-2 text-[var(--color-text-soft)]"
              onClick={() => setNavOpen(true)}
              type="button"
            >
              <Menu className="h-4 w-4" />
            </button>
            <p className="truncate text-sm font-semibold">{project.name}</p>
          </div>
        )}

        {isKnowledge ? (
          <PageHeader
            icon={KNOWLEDGE_SECTION_ICON}
            subtitle={findKnowledgeTab(activeSection)?.subtitle}
            tabs={<KnowledgeTabs activeSection={activeSection} projectId={project.id} />}
            title={KNOWLEDGE_SECTION_TITLE}
          />
        ) : activeItem && !isChat ? (
          <PageHeader
            icon={activeItem.icon}
            subtitle={activeItem.subtitle}
            title={activeItem.label}
          />
        ) : null}

        <div className={cn(isChat && 'min-h-0 flex-1')}>
          <Outlet
            context={{ project, openNav: () => setNavOpen(true) } satisfies ProjectOutletContext}
          />
        </div>
      </div>
    </div>
  )
}
